import React, { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { prepareRegressionData, logisticRegression } from '@utils/logisticAnalysis.js';

const FlashProbabilityChart = ({ 
  data, 
  climbingStyle, 
  formatGradeWithStyle, 
  yearMode = 'all',
  useLogOdds,
  gradeSystem 
}) => {
  const chartData = useMemo(() => {
    if (!data?.length) return [];

    const regressionData = prepareRegressionData(data, climbingStyle, yearMode);
    if (!regressionData) return [];

    const { x, y, years, gradeStats } = regressionData;

    const empiricalPoints = Object.entries(gradeStats)
      .filter(([_, stat]) => {
        if (stat.totalRoutes.size < 3) return false;
        
        if (useLogOdds) {
          const prob = stat.flashedRoutes.size / stat.totalRoutes.size;
          return prob > 0 && prob < 1;
        }
        
        return true;
      })
      .map(([grade, stat]) => {
        const prob = stat.flashedRoutes.size / stat.totalRoutes.size;
        const logOdds = prob > 0 && prob < 1 ? Math.log(prob / (1 - prob)) : null;
        return {
          grade: Number(grade),
          empiricalProb: prob,
          empiricalLogOdds: logOdds,
          totalRoutes: stat.totalRoutes.size,
          flashes: stat.flashedRoutes.size
        };
      })
      .sort((a, b) => a.grade - b.grade);

    const regressionResult = logisticRegression(x, y, years, climbingStyle, yearMode === 'all');
    if (!regressionResult) {
      return empiricalPoints;
    }

    const { m, b } = regressionResult;
    const intercept = yearMode === 'all' ? b['all'] : b[yearMode];

    if (!m || !intercept) {
      return empiricalPoints;
    }

    const minGrade = Math.floor(Math.min(...empiricalPoints.map(p => p.grade))) - 1;
    const maxGrade = Math.ceil(Math.max(...empiricalPoints.map(p => p.grade))) + 1;
    const curvePoints = [];
    
    for (let x = minGrade; x <= maxGrade; x += 0.1) {
      const logit = m * (x - intercept);
      const prob = 1 / (1 + Math.exp(-logit));
      curvePoints.push({
        grade: x,
        modelProb: prob,
        modelLogOdds: logit
      });
    }

    return [
      ...curvePoints,
      ...empiricalPoints.map(point => ({
        ...point,
        isEmpirical: true
      }))
    ].sort((a, b) => a.grade - b.grade);
  }, [data, climbingStyle, useLogOdds, yearMode]);

  const tooltipFormatter = (value, name, props) => {
    if (value === null || value === undefined) return ['-', name];

    // name comes from the component's name prop ("Data" or "Model"), not the dataKey
    if (name === 'Data') {
      const item = props.payload;
      if (useLogOdds) {
        return [`${value.toFixed(2)}`, 'Empirical Log Odds'];
      }
      return [`${(value * 100).toFixed(1)}% (${item.flashes}/${item.totalRoutes})`, 'First Go Success Rate'];
    }
    if (name === 'Model') {
      if (useLogOdds) {
        return [`${value.toFixed(2)}`, 'Model Log Odds'];
      }
      return [`${(value * 100).toFixed(1)}%`, 'Model Probability'];
    }
    return [value, name];
  };
  
  const memoizedTickFormatter = useMemo(() => {
    return (value) => formatGradeWithStyle(value, gradeSystem);
  }, [formatGradeWithStyle, gradeSystem]);

  const tooltipLabelFormatter = useMemo(() => {
    return (value) => {
      const rounded = Math.round(value * 100) / 100;
      return formatGradeWithStyle(rounded, gradeSystem);
    };
  }, [formatGradeWithStyle, gradeSystem]);

  if (!chartData.length) {
    return <div className="text-center py-8">Not enough data to calculate first-go probabilities</div>;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart 
        data={chartData} 
        margin={{ 
          top: 5,
          right: 0,
          left: 10,
          bottom: gradeSystem === 'ewbank' ? 22 : 42
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="grade"
          type="number"
          domain={['dataMin', 'dataMax']}
          ticks={[...new Set(chartData.filter(d => Number.isInteger(d.grade)).map(d => d.grade))]}
          interval={0}
          tickFormatter={memoizedTickFormatter}
          label={{
            value: `Grade (${climbingStyle === 'boulder' ? 'V-Grade' : gradeSystem})`,
            position: 'bottom',
            offset: gradeSystem === 'ewbank' ? 8 : 28
          }}
          angle={gradeSystem === 'ewbank' ? 0 : -45}
          textAnchor={gradeSystem === 'ewbank' ? "middle" : "end"}
        />
        <YAxis
          domain={useLogOdds ? ['auto', 'auto'] : [0, 1]}
          tickFormatter={useLogOdds ? 
            (value) => value.toFixed(1) :
            (value) => `${(value * 100).toFixed(0)}%`
          }
          label={{
            value: useLogOdds ? 'Log Odds' : 'First Go Success Rate',
            angle: -90,
            position: 'middle',
            dx: -28
          }}
        />
        <Tooltip
          formatter={tooltipFormatter}
          labelFormatter={tooltipLabelFormatter}
          cursor={{ fill: 'transparent' }}
        />
        <Legend 
          align="right" 
          verticalAlign="middle" 
          layout="vertical"
          wrapperStyle={{ 
            paddingLeft: "10px",
          }}
        />
        <Scatter
          name="Data"
          dataKey={useLogOdds ? "empiricalLogOdds" : "empiricalProb"}
          fill="#8884d8"
          shape="circle"
        />
        <Line
          name="Model"
          type="monotone"
          dataKey={useLogOdds ? "modelLogOdds" : "modelProb"}
          stroke="#82ca9d"
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default FlashProbabilityChart;